import { useReportPageLoadedFromLinkClick } from '@ms/yammer-hooks/dist/pageLoad';
import { useEndSearchSessionEffect } from '@ms/yammer-hooks/dist/searchSession';
import { useTranslations } from '@ms/yammer-i18n-react';
import CommonStrings from '@ms/yammer-strings/dist/Common';
import PanesStrings from '@ms/yammer-strings/dist/Panes';
import AppNavigation from '@ms/yammer-web-components/dist/navigation/AppNavigation';
import React, { FC, ReactNode, Suspense, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import SuiteHeader from '../SuiteHeader';

import LoadableWebSubHeader from './LoadableWebSubHeader';
import PageLayout from './PageLayoutSwitcher';
import { useGetBannersCallback } from './hooks/useGetBannersCallback';

interface RoutePageLayoutProps {
  readonly collapseLeftSidebar?: boolean;
  readonly noScroll?: boolean;
  readonly noSpacing?: boolean;
  readonly isLeftSidebarEnabled?: boolean;
  onRenderFooter?(): ReactNode;
}

const Page: FC<RoutePageLayoutProps> = ({
  collapseLeftSidebar = false,
  noScroll = false,
  noSpacing = false,
  isLeftSidebarEnabled = true,
  onRenderFooter,
  children,
}) => {
  const t = useTranslations(PanesStrings);
  const commonStrings = useTranslations(CommonStrings);
  const { pathname, search: queryString } = useLocation();
  const pathQueryString = `${pathname}${queryString}`;

  const getHeader = useCallback(() => <SuiteHeader />, []);

  const getBanners = useGetBannersCallback();

  const getSubHeader = useCallback(
    () => (
      <Suspense fallback={null}>
        <LoadableWebSubHeader />
      </Suspense>
    ),
    []
  );
  const getNavigation = useCallback(() => <AppNavigation />, []);

  useReportPageLoadedFromLinkClick();
  useEndSearchSessionEffect();

  const navigation = isLeftSidebarEnabled ? getNavigation : undefined;

  return (
    <PageLayout
      closeContentSidebarAriaLabel={commonStrings.commonClose()}
      collapseLeftSidebar={collapseLeftSidebar}
      noScroll={noScroll}
      noSpacing={noSpacing}
      onRenderBanner={getBanners}
      onRenderHeader={getHeader}
      onRenderLeftSidebar={navigation}
      onRenderSubHeader={getSubHeader}
      onRenderFooter={onRenderFooter}
      resetState={pathQueryString}
      showContentSidebarAriaLabel={t.showAdditionalInformationPane()}
    >
      {children}
    </PageLayout>
  );
};

export default Page;
